import { memo, useState, useCallback, useEffect } from "react";
import { Tag, Card, Button, Spin, Row, Col, Tooltip, Space, Alert } from "antd/es";
import { deepEqualsDebugMemo, downloadBlob, downloadFile, stringToBlob } from "../util";
import CardTitle from "./CardTitle";
import { DeleteOutlined, DownloadOutlined, ExclamationOutlined, ExportOutlined, FolderOpenOutlined, HourglassOutlined, ImportOutlined, ReloadOutlined, SaveFilled, UpOutlined } from "@ant-design/icons";
import { Hoverable } from "./Hoverable";
import { deleteInferenceTask, queueImageGeneration, restartInferenceServer, setInferencePriority, truncateInferenceQueue } from "../api";
import { fetchAuth } from "../authentication";
import useModal from "antd/es/modal/useModal";


export const InferenceQueueCard = memo(({ setQueueSize }) => {
    const [collapsed, setCollapsed] = useState(true);
    const [queue, setQueue] = useState([]);
    const [lastKey, setLastKey] = useState(null);

    const [showModal, ModalCtx] = useModal();

    const updateQueue = useCallback(() => {
        fetchAuth("https://ctlzr-api.bitgate.workers.dev/api/v1/jobs/inference?h=" + Math.random())
            .then(resp => resp.json())
            .then(tasks => tasks.map(task => ({ ...task, data: JSON.parse(task.data) })))
            .then(tasks => {
                tasks.sort((a, b) => b.key - a.key);

                const completed = tasks.filter(t => !!t.result);

                if (completed.length && completed[0].key !== lastKey) {
                    if (lastKey !== null) {
                        window.dispatchEvent(new CustomEvent('custom.lastResult', { detail: { urls: completed[0].result, args: completed[0].data } }));
                    }

                    setLastKey(completed[0].key);
                }

                const q = tasks.filter(t => !t.result);
                q.sort((a, b) => a.key - b.key);

                setQueue(q);
                setQueueSize(q.length);
            });
    }, [setQueueSize, lastKey]);

    const nukeQueue = (skipConfirmation = false) => {
        if (skipConfirmation || window.confirm("Are you sure you want to delete the entire queue?")) {
            return truncateInferenceQueue().then(() => updateQueue());
        } else {
            return Promise.resolve();
        }
    };

    const restartServer = () => {
        if (window.confirm("Are you sure you want to restart the server?")) {
            restartInferenceServer(false);
            restartInferenceServer(true);
        }
    };

    const exportQueue = () => {
        downloadBlob(stringToBlob(JSON.stringify(queue, null, 4)), "queue-export.json", true);
    }


    const importIntoQueue = async () => {
        const options = {
            types: [
                {
                    description: "Queue JSON data",
                    accept: {
                        "application/json": [".json"],
                    },
                },
            ],
            excludeAcceptAllOption: true,
            multiple: false,
        };

        const [chosen] = await window.showOpenFilePicker(options);
        if (chosen) {
            const fileData = await chosen.getFile();
            const poep = await fileData.text();
            const queueData = JSON.parse(poep);

            if (!Array.isArray(queueData)) {
                alert('Invalid queue data.');
                return;
            }

            // Verify items
            for (const item of queueData) {
                if (!item.key || !item.data) {
                    alert("Invalid queue item in array");
                    return;
                }
            }

            const firstKey = Date.now() - 5000; // Sneak out 5K keys over 5 seconds, we can do this I'm sure. :)

            // Filter ones that were processing
            const submissions = queueData.filter(q => !(q.result || q.processing) && q.data)
                .map((q, idx) => ({ ...q.data, key: `${firstKey + idx}` }));

            const proceed = async (replaceQueue) => {
                if (replaceQueue) {
                    await nukeQueue(true);
                    await queueImageGeneration(submissions);
                    window.alert("Queue replaced successfully.");
                } else {
                    await queueImageGeneration(submissions);
                }
            };


            let modalPromise = showModal.info({
                width: 500,
                maskClosable: true,
                footer: null,
                title: "JSON queue import",
                closable: true,
                okCancel: false,

                cancelText: "nvm",
                content: (<Space size="large" direction="vertical" >
                    <Alert type="warning" banner message={<>You are about to import <b>{submissions.length}</b> queue tasks.</>} />
                    <div>Do you want to replace the <b>ENTIRE</b> queue with your loaded data, or do you want to append it at the end?</div>

                    <Space>
                        <Button type="default" onClick={() => { modalPromise.destroy(); proceed(false) }}>Add to queue end</Button>
                        <Button danger onClick={() => { modalPromise.destroy(); proceed(true) }} icon={<ExclamationOutlined />}>Replace ENTIRE queue</Button>
                    </Space>
                </Space>),
            });
        }
    }

    useEffect(() => {
        const interval = setInterval(() => updateQueue(), 2000);
        updateQueue();

        return () => clearInterval(interval);
    }, [updateQueue]);


    const deleteQueueItem = useCallback((key) => {
        // setDeleting(true);
        deleteInferenceTask(key)
            .then(updateQueue)
        // .finally(() => setDeleting(false));
    }, []);

    const queueLenTag = (<Tag color="processing" style={{ marginLeft: 4, padding: '0px 6px', fontSize: 12 }}>{queue.length}</Tag>);

    return (
        <Card bodyStyle={collapsed ? { display: 'none' } : { maxHeight: 220, overflowY: 'auto' }}
            title={<CardTitle
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                icon={<HourglassOutlined />}
                style={{ widht: '100%' }}
            >Inference Queue {queueLenTag}{' '}
                {queue.length > 0 && <Button size="small" type="danger" onClick={() => nukeQueue()} icon={<DeleteOutlined />} />}{' '}
                <Button size="small" type="text" color="danger" onClick={restartServer} icon={<ReloadOutlined />} />

                <span style={{ float: 'right' }}>
                    <Tooltip title="Export queue contents">
                        <Button size="small" type="text" onClick={exportQueue} icon={<SaveFilled />} />
                    </Tooltip>

                    <Tooltip title="Import and choose action...">
                        <Button size="small" type="text" onClick={importIntoQueue} icon={<DownloadOutlined />} />
                    </Tooltip>
                </span>
            </CardTitle>}
            className="run-container settings-card inf-queue-card" style={{ width: '100%' }}
        >
            <Row gutter={[12, 12]}>
                <Col xxl={24} className="inf-queue">
                    {collapsed ? null : queue.map(task => <QueueItem key={task.key} task={task} deleteQueueItem={deleteQueueItem} />)}
                </Col>
            </Row>

            {ModalCtx}
        </Card>
    )
}, deepEqualsDebugMemo("InferenceQueueCard"));

const QueueItem = memo(function QueueItem(props) {
    const { task, deleteQueueItem } = props;
    const { processing, key, priority = 1 } = task;
    const { steps, cfg, prompt } = task.data;

    const setPriority = (newPriority) => {
        setInferencePriority(key, newPriority);
    };

    const onClick = () => {
        window.dispatchEvent(new CustomEvent('custom.setArgs', { detail: task.data }))
    }

    return (<Hoverable>
        <div className="inf-queue-item">
            <div className="inf-queue-item-prefix">
                {processing ? <Spin spinning={false} /> : <Button size="small" loading={false} type="outline" icon={<DeleteOutlined />} onClick={() => deleteQueueItem(key)} />}
            </div>

            <Tag color="processing" className="inf-queue-steps">{steps}</Tag>
            <Tag color="processing" className="inf-queue-cfg">{cfg}</Tag>

            <div className="inf-queue-prompt" onClick={onClick}>{prompt}</div>

            <div className="inf-queue-actions">
                <Button size="small" type="primary" icon={<UpOutlined />} onClick={() => setPriority(priority + 1)}>{priority !== 1 ? ` ${priority}` : null}</Button>
            </div>
        </div>
    </Hoverable >)
}, (prev, next) => prev !== next || !prev || !next || prev.processing !== next.processing || prev.priority !== next.priority);

